import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Collapse,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircleOutline,
  ExpandMore,
  ExpandLess,
  CloudUploadOutlined,
  Close,
} from '@mui/icons-material';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import type { SupportCategory, SupportFormData, SupportScreenshot } from '../../types';
import type { SubmitStatus } from '../../hooks/useSupportForm';

interface SupportFormProps {
  formData: SupportFormData;
  setField: <K extends keyof SupportFormData>(key: K, value: SupportFormData[K]) => void;
  onBack: () => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  submitStatus: SubmitStatus;
  error: string | null;
  researcherName: string;
  researcherEmail: string;
  detectedStudyName: string;
  addScreenshots: (files: File[]) => void;
  removeScreenshot: (id: string) => void;
  studies: Array<{ id: string; name: string }>;
}

const CATEGORY_LABELS: Record<SupportCategory, string> = {
  'login': "Can't Log In",
  'study-dashboard': 'Study or Dashboard Issue',
  'data-extraction': 'Data Extraction / Download Error',
  'enrollment': 'Participant Enrollment Problem',
  'other': 'Other / Feature Request',
};

const DESCRIPTION_PLACEHOLDERS: Record<SupportCategory, string> = {
  'login': 'What happens when you try to log in?',
  'study-dashboard': 'e.g., My study disappeared from the dashboard, I\'m seeing a \'Forbidden\' error...',
  'data-extraction': 'e.g., Only 1 of 7 participants downloaded successfully, getting \'Out of shared memory\'...',
  'enrollment': 'Describe the enrollment issue you\'re experiencing...',
  'other': 'Tell us what\'s on your mind...',
};

const BROWSER_OPTIONS = [
  'Chrome',
  'Firefox',
  'Safari',
  'Edge',
  'Mobile Safari',
  'Mobile Chrome',
  'Other',
];

const ENROLLMENT_STEPS = [
  'Email verification',
  'Fitbit authorization',
  'Information collection',
  'Other',
];

const MotionBox = motion.create(Box);

const SupportForm: React.FC<SupportFormProps> = ({
  formData,
  setField,
  onBack,
  onSubmit,
  isSubmitting,
  submitStatus,
  error,
  researcherName,
  researcherEmail,
  detectedStudyName,
  addScreenshots,
  removeScreenshot,
  studies,
}) => {
  const theme = useTheme();
  const shouldReduceMotion = useReducedMotion();
  const [showMore, setShowMore] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const category = formData.category as SupportCategory;

  const showStudySelector = category === 'study-dashboard' || category === 'data-extraction' || category === 'enrollment';
  const showScreenshotInMain = category !== 'login';
  const showBrowserDevice = category === 'login';
  const showUrgency = category === 'login';
  const showParticipantsAffected = category === 'data-extraction';
  const showErrorMessage = category === 'data-extraction';
  const showParticipantIdentifier = category === 'enrollment';
  const showEnrollmentStep = category === 'enrollment';

  // Clipboard paste handler
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) imageFiles.push(file);
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        addScreenshots(imageFiles);
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [addScreenshots]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const images = acceptedFiles.filter((f) => f.type.startsWith('image/'));
      if (images.length > 0) addScreenshots(images);
    },
    [addScreenshots]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
    maxFiles: 3 - formData.screenshots.length,
    maxSize: 5 * 1024 * 1024,
    disabled: formData.screenshots.length >= 3,
    noClick: false,
    noKeyboard: false,
  });

  const fieldVariants = {
    hidden: shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 10 },
    visible: (i: number) =>
      shouldReduceMotion
        ? { opacity: 1 }
        : {
            opacity: 1,
            y: 0,
            transition: {
              delay: i * 0.05,
              type: 'spring' as const,
              damping: 20,
              stiffness: 300,
            },
          },
  };

  // Success state
  if (submitStatus === 'success') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1.5 }}>
        <motion.div
          initial={shouldReduceMotion ? { scale: 1 } : { scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', damping: 10, stiffness: 200 }}
        >
          <CheckCircleOutline sx={{ fontSize: 56, color: theme.palette.success.main }} />
        </motion.div>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Sent!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We'll get back to you within 24 hours.
        </Typography>
      </Box>
    );
  }

  let fieldIndex = 0;

  return (
    <Box ref={panelRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header with back button + category label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={onBack} aria-label="Back to categories">
          <ArrowBack fontSize="small" />
        </IconButton>
        <Chip
          label={CATEGORY_LABELS[category]}
          size="small"
          sx={{
            backgroundColor: `${theme.palette.primary.main}10`,
            color: theme.palette.primary.main,
            fontWeight: 500,
          }}
        />
      </Box>

      {/* Pre-populated context as read-only chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        <Chip
          label={researcherName}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
        <Chip
          label={researcherEmail}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
        {detectedStudyName && (
          <Chip
            label={detectedStudyName}
            size="small"
            variant="outlined"
            color="secondary"
            sx={{ fontSize: '0.75rem' }}
          />
        )}
      </Box>

      {/* Description — always shown */}
      <MotionBox
        variants={fieldVariants}
        initial="hidden"
        animate="visible"
        custom={fieldIndex++}
      >
        <TextField
          label="Description"
          placeholder={DESCRIPTION_PLACEHOLDERS[category]}
          multiline
          minRows={3}
          fullWidth
          required
          value={formData.description}
          onChange={(e) => setField('description', e.target.value)}
          inputProps={{ maxLength: 5000 }}
        />
      </MotionBox>

      {/* Category-specific fields */}
      <AnimatePresence mode="wait">
        {showStudySelector && !detectedStudyName && (
          <MotionBox
            key="study-selector"
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={fieldIndex++}
          >
            <FormControl fullWidth size="small">
              <InputLabel>Study</InputLabel>
              <Select
                value={formData.studyId}
                label="Study"
                onChange={(e) => {
                  const study = studies.find((s) => s.id === e.target.value);
                  setField('studyId', e.target.value as string);
                  setField('studyName', study?.name || '');
                }}
              >
                {studies.length === 0 && (
                  <MenuItem value="" disabled>
                    No studies available
                  </MenuItem>
                )}
                {studies.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </MotionBox>
        )}

        {showBrowserDevice && (
          <MotionBox
            key="browser-device"
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={fieldIndex++}
          >
            <FormControl fullWidth size="small">
              <InputLabel>Browser / Device</InputLabel>
              <Select
                value={formData.browserDevice}
                label="Browser / Device"
                onChange={(e) => setField('browserDevice', e.target.value as string)}
              >
                {BROWSER_OPTIONS.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </MotionBox>
        )}

        {showParticipantsAffected && (
          <MotionBox
            key="participants-affected"
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={fieldIndex++}
          >
            <TextField
              label="Participants affected"
              type="number"
              size="small"
              fullWidth
              value={formData.participantsAffected}
              onChange={(e) => setField('participantsAffected', e.target.value)}
              inputProps={{ min: 0 }}
            />
          </MotionBox>
        )}

        {showErrorMessage && (
          <MotionBox
            key="error-message"
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={fieldIndex++}
          >
            <TextField
              label="Error message"
              placeholder="Paste the error message you see"
              size="small"
              fullWidth
              value={formData.errorMessage}
              onChange={(e) => setField('errorMessage', e.target.value)}
              inputProps={{ maxLength: 2000 }}
            />
          </MotionBox>
        )}

        {showParticipantIdentifier && (
          <MotionBox
            key="participant-id"
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={fieldIndex++}
          >
            <TextField
              label="Participant identifier"
              placeholder="Email or study code (if known)"
              size="small"
              fullWidth
              value={formData.participantIdentifier}
              onChange={(e) => setField('participantIdentifier', e.target.value)}
            />
          </MotionBox>
        )}

        {showEnrollmentStep && (
          <MotionBox
            key="enrollment-step"
            variants={fieldVariants}
            initial="hidden"
            animate="visible"
            custom={fieldIndex++}
          >
            <FormControl fullWidth size="small">
              <InputLabel>Enrollment step</InputLabel>
              <Select
                value={formData.enrollmentStep}
                label="Enrollment step"
                onChange={(e) => setField('enrollmentStep', e.target.value as string)}
              >
                {ENROLLMENT_STEPS.map((step) => (
                  <MenuItem key={step} value={step}>
                    {step}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </MotionBox>
        )}
      </AnimatePresence>

      {/* Urgency checkbox */}
      {showUrgency && (
        <MotionBox
          variants={fieldVariants}
          initial="hidden"
          animate="visible"
          custom={fieldIndex++}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.urgency}
                onChange={(e) => setField('urgency', e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                I have a participant coming in soon — this is urgent
              </Typography>
            }
          />
        </MotionBox>
      )}

      {/* Screenshot upload (shown inline for most categories) */}
      {showScreenshotInMain && (
        <MotionBox
          variants={fieldVariants}
          initial="hidden"
          animate="visible"
          custom={fieldIndex++}
        >
          <ScreenshotUploadSection
            screenshots={formData.screenshots}
            onRemove={removeScreenshot}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragActive={isDragActive}
            disabled={formData.screenshots.length >= 3}
          />
        </MotionBox>
      )}

      {/* "Add more details" expandable section */}
      <Box>
        <Button
          size="small"
          onClick={() => setShowMore(!showMore)}
          startIcon={showMore ? <ExpandLess /> : <ExpandMore />}
          sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem' }}
        >
          {showMore ? 'Hide additional details' : 'Add more details'}
        </Button>
        <Collapse in={showMore}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Additional notes"
              multiline
              minRows={2}
              fullWidth
              size="small"
              value={formData.additionalNotes}
              onChange={(e) => setField('additionalNotes', e.target.value)}
              inputProps={{ maxLength: 5000 }}
            />
            {!showScreenshotInMain && (
              <ScreenshotUploadSection
                screenshots={formData.screenshots}
                onRemove={removeScreenshot}
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                isDragActive={isDragActive}
                disabled={formData.screenshots.length >= 3}
              />
            )}
          </Box>
        </Collapse>
      </Box>

      {/* Error display */}
      {submitStatus === 'error' && error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onSubmit}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Submit button */}
      <motion.div whileTap={shouldReduceMotion ? undefined : { scale: 0.97 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={onSubmit}
          disabled={!formData.description.trim() || isSubmitting}
          sx={{
            py: 1.2,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {isSubmitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              Sending...
            </Box>
          ) : (
            'Send Support Request'
          )}
        </Button>
      </motion.div>
    </Box>
  );
};

// Screenshot upload sub-section
interface ScreenshotUploadSectionProps {
  screenshots: SupportScreenshot[];
  onRemove: (id: string) => void;
  getRootProps: any;
  getInputProps: any;
  isDragActive: boolean;
  disabled: boolean;
}

const ScreenshotUploadSection: React.FC<ScreenshotUploadSectionProps> = ({
  screenshots,
  onRemove,
  getRootProps,
  getInputProps,
  isDragActive,
  disabled,
}) => {
  const theme = useTheme();

  return (
    <Box>
      {/* Thumbnails */}
      {screenshots.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          {screenshots.map((s) => (
            <Box
              key={s.id}
              sx={{
                position: 'relative',
                width: 72,
                height: 72,
                borderRadius: '6px',
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <img
                src={s.preview}
                alt="Screenshot preview"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <IconButton
                size="small"
                onClick={() => onRemove(s.id)}
                aria-label="Remove screenshot"
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  p: 0.3,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Dropzone */}
      {!disabled && (
        <Box
          {...getRootProps()}
          sx={{
            border: `1.5px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: '8px',
            p: 1.5,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? `${theme.palette.primary.main}08` : 'transparent',
            transition: 'border-color 0.2s, background-color 0.2s',
            '&:hover': {
              borderColor: theme.palette.primary.light,
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadOutlined
            sx={{ fontSize: 20, color: 'text.secondary', mb: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Drop images, paste from clipboard, or click to browse
          </Typography>
          <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: '0.65rem' }}>
            Max 3 images, 5MB each
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SupportForm;
